import { refreshSubscriptionQueries, subscriptionQueryKeys } from '@/lib/subscription-query-keys';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { accountService } from '@/services/account.service';
import { AlertTriangle, X, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { openInNewTab } from '@/lib/open-in-new-tab';

/**
 * Tarja de aviso de inadimplência. A cobrança, os dias de atraso e o limite de
 * suspensão vêm prontos do backend (`payment_delinquency.warning_charge`), que
 * só considera as cobranças da assinatura vigente (TK-1623). O botão "Pagar
 * agora" abre o checkout VIO da fatura em nova aba, sem tirar o assinante da
 * plataforma.
 */
export function SubscriptionWarningBanner() {
  const [isDismissed, setIsDismissed] = useState(false);
  // O checkout abre em outra aba e esta segue carregada: ao voltar, recarrega a
  // assinatura (o cache de 5 min manteria a tarja de uma fatura paga).
  const [checkoutOpened, setCheckoutOpened] = useState(false);
  const queryClient = useQueryClient();

  const { data: subscription } = useQuery({
    queryKey: subscriptionQueryKeys.subscriptionWarning,
    queryFn: () => accountService.getMySubscription(),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  // Só cobranças vencidas que ainda não atingiram o bloqueio definitivo (esse
  // é responsabilidade do SubscriptionGuard).
  const delinquency = subscription?.payment_delinquency;
  const warningInvoice = delinquency?.warning_charge ?? null;
  const suspensionThreshold = delinquency?.suspension_threshold_days ?? 0;
  const daysOverdue = warningInvoice?.days_overdue ?? 0;

  // Escuta só `focus` (voltar à aba dispara também `visibilitychange`, o que
  // recarregaria em dobro) e só enquanto houver fatura na tarja.
  const awaitingReturn = checkoutOpened && warningInvoice !== null;
  useEffect(() => {
    if (!awaitingReturn) return;
    const refresh = () => {
      void refreshSubscriptionQueries(queryClient);
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [awaitingReturn, queryClient]);

  if (isDismissed || !warningInvoice) {
    return null;
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Mensagem do tail varia conforme política de suspensão configurada na VIO.
  const remainingDays = Math.max(suspensionThreshold - daysOverdue, 0);
  const tailMessage =
    suspensionThreshold === 0
      ? 'O acesso está suspenso até que você realize o pagamento.'
      : remainingDays > 0
        ? `O acesso será suspenso em ${remainingDays} dia(s)!`
        : 'O acesso está suspenso até que você realize o pagamento.';

  const handlePayNow = () => {
    // Abre o checkout VIO Pay da fatura em NOVA aba — a plataforma segue
    // aberta nesta. O link já está carregado, então o clique abre direto.
    if (warningInvoice?.payment_url) {
      openInNewTab(warningInvoice.payment_url);
      setCheckoutOpened(true);
      return;
    }
    window.location.href = '/account?tab=billing';
  };

  return (
    <div className={cn(
      'bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 shadow-lg',
      'animate-in slide-in-from-top duration-300'
    )}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-full">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <span className="font-semibold">
              Fatura vencida!
            </span>
            <span className="text-sm text-white/90">
              Sua fatura de {formatCurrency(warningInvoice.amount)} venceu há{' '}
              <strong>{daysOverdue} {daysOverdue === 1 ? 'dia' : 'dias'}</strong>.{' '}
              <span className="hidden sm:inline text-amber-100 font-medium">
                {tailMessage}
              </span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-white text-orange-600 hover:bg-white/90 font-semibold shadow-sm"
            onClick={handlePayNow}
          >
            <CreditCard className="h-4 w-4 mr-1" />
            Pagar agora
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => setIsDismissed(true)}
            aria-label="Fechar aviso"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionWarningBanner;

